/*
|--------------------------------------------------------------------------
| Prepare/Consume Chain Builder
|--------------------------------------------------------------------------
|
| Utility for chaining prepare and consume functions in resourceful decorators.
| This allows combining default decorator behavior with custom logic in a
| predictable order while maintaining proper error handling.
|
*/

/**
 * Function types for prepare and consume operations
 */
export type CustomPrepareFunction = (value: unknown) => unknown
export type CustomConsumeFunction = (value: unknown) => unknown
export type DefaultPrepareFunction<T = unknown> = (
  key: string,
  value: unknown,
  nullable?: boolean
) => T
export type DefaultConsumeFunction<T = unknown> = (
  key: string,
  value: unknown,
  nullable?: boolean
) => T

/**
 * Utility class for building chained prepare and consume functions.
 *
 * The chaining order reflects the data flow:
 * - Prepare chain: Default → Custom (data flows TO database)
 * - Consume chain: Custom → Default (data flows FROM database)
 */
export class PrepareConsumeChainBuilder {
  /**
   * Creates a chained prepare function that calls default first, then custom.
   *
   * @param propertyKey - The property name for error context
   * @param nullable - Whether the field accepts null values
   * @param defaultPrepare - The default prepare function from decorator
   * @param customPrepare - The custom prepare function from options
   * @returns A chained prepare function or undefined if no functions provided
   */
  static chainPrepare<T>(
    propertyKey: string,
    nullable: boolean,
    defaultPrepare?: DefaultPrepareFunction<T> | undefined,
    customPrepare?: CustomPrepareFunction | undefined
  ): CustomPrepareFunction | undefined {
    // If neither function is provided, return undefined
    if (!defaultPrepare && !customPrepare) {
      return undefined
    }

    // If only custom function is provided, return it directly
    if (!defaultPrepare && customPrepare) {
      return customPrepare
    }

    // If only default function is provided, wrap it to match signature
    if (defaultPrepare && !customPrepare) {
      return (value: unknown) => defaultPrepare(propertyKey, value, nullable)
    }

    // Both functions provided - chain them: default → custom
    return (value: unknown) => {
      const defaultResult = defaultPrepare!(propertyKey, value, nullable)

      return customPrepare!(defaultResult)
    }
  }

  /**
   * Creates a chained consume function that calls custom first, then default.
   *
   * @param propertyKey - The property name for error context
   * @param nullable - Whether the field accepts null values
   * @param defaultConsume - The default consume function from decorator
   * @param customConsume - The custom consume function from options
   * @returns A chained consume function or undefined if no functions provided
   */
  static chainConsume<T>(
    propertyKey: string,
    nullable: boolean,
    defaultConsume?: DefaultConsumeFunction<T>,
    customConsume?: CustomConsumeFunction
  ): CustomConsumeFunction | undefined {
    // If neither function is provided, return undefined
    if (!defaultConsume && !customConsume) {
      return undefined
    }

    // If only custom function is provided, return it directly
    if (!defaultConsume && customConsume) {
      return customConsume
    }

    // If only default function is provided, wrap it to match signature
    if (defaultConsume && !customConsume) {
      return (value: unknown) => defaultConsume(propertyKey, value, nullable)
    }

    // Both functions provided - chain them: custom → default
    return (value: unknown) => {
      const customResult = customConsume!(value)

      return defaultConsume!(propertyKey, customResult, nullable)
    }
  }
}
